// @flow
import * as React from 'react';
import * as Yup from 'yup';
import { Formik, Field, Form, FormikActions } from 'formik';

// Types
import { gtmUserEventType } from 'types/gtm';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import {
  TextField,
  SelectField,
  FormikField,
  PhoneTextField,
  TextFieldWithIcon
} from 'components/api';
import { Button } from 'components/atoms';
import Modal from 'components/atoms/Modal';
import ChangePassword from './components/ChangePassword';
import DeleteAccount from './components/DeleteAccount';

// Utils
import { isPhoneNumberValid, isEmpty, rawPhoneNumber } from 'utils/string';
import { isObjEmpty } from 'utils/varValidation';
import type { PhoneType } from 'api/xws/login/entities/ContactInfo';

import './Profile.scss';

type ContactInfo = {
  phoneNumbers: PhoneType[],
  emailAddress: {
    email: string
  },
  address: {
    streetAddressOne: string,
    streetAddressTwo: string,
    city: string,
    state: string,
    country: string,
    postalCode: string
  }
};

type PersonInfo = {
  firstName: string,
  lastName: string,
  contactInfo: ContactInfo
};

type Option = {
  value: string,
  label?: string
};

type Values = {
  firstName: string,
  lastName: string,
  mobilePhoneNumber: string,
  phoneNumber: string,
  email: string,
  country: Option,
  state: Option,
  streetAddressOne: string,
  streetAddressTwo: string,
  city: string,
  postalCode: string
};

type Props = {
  personInfo: PersonInfo,
  gtmUserEvent: gtmUserEventType,
  updateProfile: (personInfo: Object) => void,
  close: () => void,
  countryCode: string,
  setFirstName: string => void,
  setLastName: string => void,
  setEmail: string => void,
  setPhoneNumber: string => void,
  setMobilePhoneNumber: string => void,
  countries: Array<Object>,
  defaultCountry: Array<Object>,
  setStreetAddressOne: string => void,
  setStreetAddressTwo: string => void,
  setCity: string => void,
  setStateAddress: Option => void,
  setPostalCode: string => void,
  setCountry: Option => void,
  states: () => void,
  profileInitialValues: Values,
  getCountryStates: string => void,
  isSocialLogin: string,
  intl: IntlShape
};

type State = {
  streetAddress: string,
  streetAddressComponents: Array<Object>,
  streetAddressModified: boolean
};

const validationSchema = (props: Props) => {
  return Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    mobilePhoneNumber: Yup.string()
      .min(0)
      .test({
        name: 'mobilePhoneNumber',
        message: 'Mobile Phone number is not valid',
        test(value?: string = '') {
          return isEmpty(value) || isPhoneNumberValid(value, props.countryCode);
        }
      }),
    phoneNumber: Yup.string()
      .required()
      .test({
        name: 'phoneNumber',
        message: 'Phone number is not valid',
        test(value?: string = '') {
          return isPhoneNumberValid(value, props.countryCode);
        }
      }),
    email: Yup.string()
      .email()
      .required(),
    country: Yup.string(),
    state: Yup.string(),
    streetAddressOne: Yup.string(),
    streetAddressTwo: Yup.string(),
    city: Yup.string(),
    postalCode: Yup.string()
  });
};

export default class Profile extends React.Component<Props, State> {
  static block = 'profile';

  constructor(props: Props) {
    super(props);
    (this: any).onSubmit = this.onSubmit.bind(this);
    (this: any).formikForm = React.createRef();
    (this: any).modalChangeMyPassword = React.createRef();
    (this: any).modalDeleteAccount = React.createRef();
    (this: any).showModalChangeMyPassword = this.showModalChangeMyPassword.bind(
      this
    );
    (this: any).showModalDeleteAccount = this.showModalDeleteAccount.bind(this);

    (this: any).state = {
      streetAddressModified: false
    };
  }

  modalChangeMyPassword: React.Ref<any>;
  modalDeleteAccount: React.Ref<any>;

  initialValues = this.props.profileInitialValues;

  updatePersonInfo(values: Values) {
    const {
      firstName,
      lastName,
      mobilePhoneNumber,
      phoneNumber,
      email,
      streetAddressOne,
      streetAddressTwo,
      city,
      state,
      postalCode,
      country
    } = values;

    return {
      firstName,
      lastName,
      contactInfo: {
        address: {
          country: (country && country.value) || '',
          state: (state && state.value) || '',
          streetAddressOne: streetAddressOne || '',
          streetAddressTwo: streetAddressTwo || '',
          city: city || '',
          postalCode: postalCode || ''
        },
        emailAddress: {
          email
        },
        phoneNumbers: [
          {
            type: 'HOME',
            digits: rawPhoneNumber(phoneNumber)
          },
          {
            type: 'MOBILE',
            digits: !isEmpty(mobilePhoneNumber)
              ? rawPhoneNumber(mobilePhoneNumber)
              : ''
          }
        ]
      }
    };
  }

  isFormModified(values: Values) {
    const { personInfo } = this.props;
    const {
      firstName,
      lastName,
      mobilePhoneNumber,
      phoneNumber,
      email,
      streetAddressOne,
      streetAddressTwo,
      city,
      state,
      postalCode,
      country
    } = values;
    const indexMobilePhoneNumber = personInfo.contactInfo.phoneNumbers
      .map(x => x.type)
      .indexOf('MOBILE');
    let existMobilePhoneNumber = !isEmpty(
      personInfo.contactInfo.phoneNumbers[indexMobilePhoneNumber].digits
    )
      ? personInfo.contactInfo.phoneNumbers[indexMobilePhoneNumber].digits
      : '';
    existMobilePhoneNumber =
      mobilePhoneNumber &&
      existMobilePhoneNumber !== rawPhoneNumber(mobilePhoneNumber);
    return (
      personInfo.firstName !== firstName ||
      personInfo.lastName !== lastName ||
      personInfo.contactInfo.phoneNumbers[0].digits !==
        rawPhoneNumber(phoneNumber) ||
      existMobilePhoneNumber ||
      personInfo.contactInfo.emailAddress.email !== email ||
      personInfo.contactInfo.address.streetAddressOne !== streetAddressOne ||
      personInfo.contactInfo.address.streetAddressTwo !== streetAddressTwo ||
      personInfo.contactInfo.address.city !== city ||
      personInfo.contactInfo.address.state !== state ||
      personInfo.contactInfo.address.postalCode !== postalCode ||
      personInfo.contactInfo.address.country !== country
    );
  }

  async onSubmit(values: Values, actions: FormikActions<Values>) {
    const { setSubmitting, setStatus } = actions;
    const {
      updateProfile,
      close,
      gtmUserEvent,
      setFirstName,
      setLastName,
      setEmail,
      setPhoneNumber,
      setMobilePhoneNumber,
      setStreetAddressOne,
      setStreetAddressTwo,
      setCity,
      setStateAddress,
      setPostalCode,
      setCountry
    } = this.props;

    const {
      firstName,
      lastName,
      phoneNumber,
      mobilePhoneNumber,
      email,
      streetAddressOne,
      streetAddressTwo,
      city,
      state,
      postalCode,
      country
    } = values;
    const personInfo = this.updatePersonInfo(values);
    const data = await updateProfile(personInfo);
    const success = data && data.success;
    setSubmitting(false);
    setStatus({ success });
    if (success) {
      setFirstName(firstName);
      setLastName(lastName);
      setEmail(email);
      setPhoneNumber(rawPhoneNumber(phoneNumber));
      setMobilePhoneNumber(mobilePhoneNumber);
      setStreetAddressOne(streetAddressOne);
      setStreetAddressTwo(streetAddressTwo);
      setCity(city);
      setStateAddress(state);
      setPostalCode(postalCode);
      setCountry(country);
      gtmUserEvent(
        'Save Profile Button',
        'Clicked',
        'Profile Info Update',
        'Profile Page > User Vehicle Page'
      );
      this.setState({
        streetAddressModified: false
      });
      close();
    }
  }

  isSaveButtonDisabled(values: Object, dirty: boolean, errors: Object) {
    const { streetAddressModified } = this.state;
    if (!streetAddressModified) {
      return !dirty || !isObjEmpty(errors) || !this.isFormModified(values);
    } else {
      return (
        !isObjEmpty(errors) ||
        !(this.isFormModified(values) || streetAddressModified)
      );
    }
  }

  showModalChangeMyPassword = (show: boolean = true) => {
    (this: any).modalChangeMyPassword.current.setShowModal(show);
  };

  showModalDeleteAccount = (show: boolean = true) => {
    (this: any).modalDeleteAccount.current.setShowModal(show);
  };

  fieldForStates = () => {
    const { states } = this.props;
    const ns = Profile.block;
    if (states) {
      const selectOptionsStates = states.map(state => {
        return { value: state.code, label: state.name };
      });
      return (
        <Field
          className={`${ns}__states-field`}
          component={SelectField}
          name="state"
          placeholder="State"
          options={selectOptionsStates}
        />
      );
    }
  };

  render() {
    const { intl } = this.props;
    return (
      <Formik
        initialValues={this.initialValues}
        validationSchema={validationSchema(this.props)}
        onSubmit={this.onSubmit}
        render={({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldTouched,
          setFieldValue,
          dirty
        }) => {
          const disabled = this.isSaveButtonDisabled(values, dirty, errors);
          // replace the next line with actual options
          const {
            countries,
            countryCode,
            states,
            getCountryStates,
            close,
            isSocialLogin
          } = this.props;
          const ns = Profile.block;
          return (
            <div>
              <Modal size={'large'} ref={this.modalChangeMyPassword}>
                {
                  <ChangePassword
                    close={() => {
                      this.showModalChangeMyPassword(false);
                    }}
                  />
                }
              </Modal>
              <Modal size={'large'} ref={this.modalDeleteAccount}>
                {
                  <DeleteAccount
                    close={() => {
                      this.showModalDeleteAccount(false);
                    }}
                    closeProfileModal={() => {
                      close();
                    }}
                  />
                }
              </Modal>
              <Form>
                <h1 className={`${ns}__title`}>
                  {intl.formatMessage({ id: 'c8.sidebar.menu.profile' })}
                </h1>
                <div className={`${ns}__form-fields`}>
                  <div className={`${ns}__form-fields__input-fields`}>
                    <div className={`${ns}__name-fields d-flex`}>
                      <FormikField
                        className={`${ns}__firstname-field`}
                        renderedComponent={TextField}
                        name="firstName"
                        placeholder={intl.formatMessage({
                          id: 'c8.common.enter.first.name'
                        })}
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.first.name',
                          defaultMessage: 'First name'
                        })}
                      />
                      <FormikField
                        renderedComponent={TextField}
                        name="lastName"
                        placeholder={intl.formatMessage({
                          id: 'c8.common.enter.last.name'
                        })}
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.last.name',
                          defaultMessage: 'Last name'
                        })}
                      />
                    </div>
                    <FormikField
                      renderedComponent={TextFieldWithIcon}
                      component={PhoneTextField}
                      countryCode={this.props.countryCode}
                      icon="oldMobile"
                      name="mobilePhoneNumber"
                      type="tel"
                      placeholder={intl.formatMessage({
                        id: 'c8.profile.mobile.phone.number'
                      })}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.mobile.phone.number',
                        defaultMessage: 'Mobile phone number'
                      })}
                    />
                    <FormikField
                      renderedComponent={TextFieldWithIcon}
                      component={PhoneTextField}
                      countryCode={this.props.countryCode}
                      icon="oldPhone"
                      name="phoneNumber"
                      type="tel"
                      placeholder={intl.formatMessage({
                        id: 'c8.common.enter.phone.mumber'
                      })}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.phone.number',
                        defaultMessage: 'Phone number'
                      })}
                    />
                    <FormikField
                      renderedComponent={TextFieldWithIcon}
                      icon="email"
                      name="email"
                      type="email"
                      placeholder={intl.formatMessage({
                        id: 'c8.common.enter.email'
                      })}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.email.address',
                        defaultMessage: 'Email address'
                      })}
                    />
                  </div>
                  <br />
                  <div className={`${ns}__form-fields__input-fields`}>
                    <SelectField
                      options={countries}
                      placeholder={intl.formatMessage({
                        id: 'c8.profile.country'
                      })}
                      name={'country'}
                      onBlur={() => setFieldTouched('country', true)}
                      onChange={value => {
                        setFieldValue('country', value);
                        setFieldValue('state', { value: '' });
                        getCountryStates(value.value);
                      }}
                      value={values['country'] || countryCode}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.country',
                        defaultMessage: 'Country'
                      })}
                    />
                    <FormikField
                      renderedComponent={TextField}
                      name="streetAddressOne"
                      placeholder={intl.formatMessage({
                        id: 'c8.profile.street.address'
                      })}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.street.address',
                        defaultMessage: 'Street Address'
                      })}
                    />
                    <FormikField
                      renderedComponent={TextField}
                      name="streetAddressTwo"
                      placeholder={intl.formatMessage({
                        id: 'c8.profile.apartment.number.suite'
                      })}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.apartment.number.suite',
                        defaultMessage: 'Apartment number, Suite'
                      })}
                    />
                    <FormikField
                      renderedComponent={TextField}
                      name="city"
                      placeholder={intl.formatMessage({
                        id: 'c8.profile.city'
                      })}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.city',
                        defaultMessage: 'City'
                      })}
                    />
                    <div className={`${ns}__address-fields d-flex`}>
                      <SelectField
                        options={states}
                        placeholder={intl.formatMessage({
                          id: 'c8.profile.state'
                        })}
                        labelKey="state"
                        name={'state'}
                        onBlur={() => setFieldTouched('state', true)}
                        onChange={value => {
                          setFieldValue('state', value);
                        }}
                        value={values['state']}
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.state',
                          defaultMessage: 'State'
                        })}
                      />
                      <FormikField
                        renderedComponent={TextField}
                        name="postalCode"
                        placeholder={intl.formatMessage({
                          id: 'c8.profile.zip'
                        })}
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.postal.code',
                          defaultMessage: 'Postal code'
                        })}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className={`${ns}__profile-buttons mb-auto d-flex flex-md-row-reverse flex-column`}
                >
                  <Button
                    type="Submit"
                    className={`${ns}__profile-buttons__save-button`}
                    disabled={disabled}
                  >
                    {intl.formatMessage({
                      id: 'c8.common.save'
                    })}
                    {isSubmitting && (
                      <div className="ld ld-ring ld-spin-fast ml-2" />
                    )}
                  </Button>
                  <div
                    className={`${ns}__profile-buttons__link-button d-flex align-items-center flex-column`}
                  >
                    {!isSocialLogin && (
                      <Button
                        className={`${ns}__profile-buttons__link-button--change-password`}
                        buttonTheme="link"
                        onClick={() => {
                          this.showModalChangeMyPassword();
                        }}
                      >
                        {intl.formatMessage({
                          id: 'c8.profile.change.my.password'
                        })}
                      </Button>
                    )}
                    <Button
                      className={`${ns}__profile-buttons__link-button--deactivate`}
                      buttonTheme="link"
                      onClick={() => {
                        this.showModalDeleteAccount();
                      }}
                    >
                      {intl.formatMessage({
                        id: 'c8.profile.deactivate.my.account'
                      })}
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          );
        }}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/pages/User/Profile/Profile.js